import {
  Box, Card, CardContent, Typography, Button, Table, TableBody,
  TableCell, TableHead, TableRow, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid, MenuItem, Switch,
  Avatar,
} from '@mui/material';
import { Add, PersonAdd, LockReset } from '@mui/icons-material';
import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import api from '../../api/axios';
import { showSnackbar } from '../ui/uiSlice';

const roleColors = { owner: 'primary', manager: 'info', cashier: 'default', super_admin: 'error' };

export default function UsersPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user: me } = useSelector(s => s.auth);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pwdDialog, setPwdDialog] = useState(false);
  const [resetDialog, setResetDialog] = useState(null); // user object to reset
  const [newPassword, setNewPassword] = useState('');
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const { register: regPwd, handleSubmit: submitPwd, reset: resetPwd, watch: watchPwd } = useForm();

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get('/users');
    setUsers(data.data); setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSubmit = async (data) => {
    try {
      await api.post('/users', data);
      dispatch(showSnackbar({ message: 'User created', severity: 'success' }));
      setDialogOpen(false); reset(); load();
    } catch (e) {
      dispatch(showSnackbar({ message: e.response?.data?.message || 'Error', severity: 'error' }));
    }
  };

  const toggleStatus = async (id) => {
    await api.put(`/users/${id}/toggle`);
    dispatch(showSnackbar({ message: 'Status updated', severity: 'success' }));
    load();
  };

  const doResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      dispatch(showSnackbar({ message: 'Password must be at least 8 characters', severity: 'error' }));
      return;
    }
    try {
      await api.post(`/users/${resetDialog.id}/reset-password`, { new_password: newPassword });
      dispatch(showSnackbar({ message: `Password reset for ${resetDialog.name}`, severity: 'success' }));
      setResetDialog(null); setNewPassword('');
    } catch (e) {
      dispatch(showSnackbar({ message: e.response?.data?.message || 'Error', severity: 'error' }));
    }
  };

  const onChangePassword = async (data) => {
    try {
      await api.put('/users/me/password', data);
      dispatch(showSnackbar({ message: 'Password updated', severity: 'success' }));
      setPwdDialog(false); resetPwd();
    } catch (e) {
      dispatch(showSnackbar({ message: e.response?.data?.message || 'Error', severity: 'error' }));
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>{t('nav.users')}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => { resetPwd(); setPwdDialog(true); }}>Change My Password</Button>
          {['owner', 'super_admin'].includes(me?.role) && (
            <Button variant="contained" startIcon={<PersonAdd />} onClick={() => { reset(); setDialogOpen(true); }}>Add User</Button>
          )}
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>User</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell>{t('common.status')}</TableCell>
              {['owner', 'super_admin'].includes(me?.role) && <TableCell>Toggle</TableCell>}
              {['owner', 'super_admin'].includes(me?.role) && <TableCell>Reset Password</TableCell>}
            </TableRow></TableHead>
            <TableBody>
              {users.map(u => (
                <TableRow key={u.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 13 }}>{u.name?.[0]?.toUpperCase()}</Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{u.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell><Chip label={u.role} size="small" color={roleColors[u.role] || 'default'} sx={{ textTransform: 'capitalize' }} /></TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {u.last_login_at ? new Date(u.last_login_at).toLocaleString('en-PK') : 'Never'}
                    </Typography>
                  </TableCell>
                  <TableCell><Chip label={u.is_active ? 'Active' : 'Inactive'} size="small" color={u.is_active ? 'success' : 'default'} /></TableCell>
                  {['owner', 'super_admin'].includes(me?.role) && (
                    <TableCell>
                      {u.id !== me?.id && (
                        <Switch size="small" checked={!!u.is_active} onChange={() => toggleStatus(u.id)} />
                      )}
                    </TableCell>
                  )}
                  {['owner', 'super_admin'].includes(me?.role) && (
                    <TableCell>
                      {u.id !== me?.id && (
                        <Button size="small" startIcon={<LockReset />} color="warning"
                          onClick={() => { setResetDialog(u); setNewPassword(''); }}>
                          Reset
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add User dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add User</DialogTitle>
        <DialogContent>
          <Box component="form" id="user-form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}><TextField fullWidth label="Full Name *" {...register('name', { required: true })} error={!!errors.name} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="Email *" {...register('email', { required: true })} error={!!errors.email} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="Phone" {...register('phone')} /></Grid>
              <Grid item xs={6}>
                <TextField fullWidth select label="Role *" defaultValue="cashier" {...register('role', { required: true })}>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="cashier">Cashier</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}><TextField fullWidth label="Password *" type="password" {...register('password', { required: true, minLength: { value: 8, message: 'Min 8 chars' } })} error={!!errors.password} helperText={errors.password?.message} /></Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" type="submit" form="user-form">Create User</Button>
        </DialogActions>
      </Dialog>

      {/* Admin reset user password dialog */}
      <Dialog open={!!resetDialog} onClose={() => setResetDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Password — {resetDialog?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set a new password for this user. They can change it after logging in.
          </Typography>
          <TextField
            fullWidth label="New Password" type="password"
            value={newPassword} onChange={e => setNewPassword(e.target.value)}
            inputProps={{ minLength: 8 }}
            helperText="Minimum 8 characters"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialog(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={doResetPassword}
            disabled={newPassword.length < 8}>
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password dialog */}
      <Dialog open={pwdDialog} onClose={() => setPwdDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Box component="form" id="pwd-form" onSubmit={submitPwd(onChangePassword)} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField fullWidth label="Current Password" type="password" {...regPwd('current_password', { required: true })} />
            <TextField fullWidth label="New Password" type="password" {...regPwd('new_password', { required: true, minLength: { value: 8, message: 'Min 8 chars' } })} />
            <TextField fullWidth label="Confirm New Password" type="password" {...regPwd('confirm', { validate: v => v === watchPwd('new_password') || 'Passwords do not match' })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPwdDialog(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" type="submit" form="pwd-form">Update</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
